import { apiClient } from '@shared/api/client';
import { ApiResponse } from '@shared/api/api.types';
import {
  QuestionDTO,
  CreateQuestionDTO,
  UpdateQuestionDTO,
  QuestionListDTO,
  ReorderQuestionsDTO,
  ReorderQuestionsResponseDTO,
  QuestionStatsDTO,
  ImportQuestionsDTO,
  ImportQuestionsResponseDTO,
} from '../dtos';

// Question Management API
export const questionApi = {
  // Get questions by survey
  async getQuestionsBySurvey(surveyId: string): Promise<ApiResponse<QuestionListDTO>> {
    return apiClient.get<QuestionListDTO>(`/surveys/${surveyId}/questions`);
  },

  // Get question by ID
  async getQuestion(surveyId: string, questionId: string): Promise<ApiResponse<QuestionDTO>> {
    return apiClient.get<QuestionDTO>(`/surveys/${surveyId}/questions/${questionId}`);
  },

  // Create question
  async createQuestion(
    surveyId: string,
    data: CreateQuestionDTO
  ): Promise<ApiResponse<QuestionDTO>> {
    return apiClient.post<QuestionDTO>(`/surveys/${surveyId}/questions`, data);
  },

  // Update question
  async updateQuestion(
    surveyId: string,
    questionId: string,
    data: UpdateQuestionDTO
  ): Promise<ApiResponse<QuestionDTO>> {
    return apiClient.put<QuestionDTO>(`/surveys/${surveyId}/questions/${questionId}`, data);
  },

  // Delete question
  async deleteQuestion(surveyId: string, questionId: string): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/surveys/${surveyId}/questions/${questionId}`);
  },

  // Reorder questions
  async reorderQuestions(
    surveyId: string,
    data: ReorderQuestionsDTO
  ): Promise<ApiResponse<ReorderQuestionsResponseDTO>> {
    return apiClient.put<ReorderQuestionsResponseDTO>(
      `/surveys/${surveyId}/questions/reorder`,
      data
    );
  },

  // Get question statistics
  async getQuestionStats(surveyId: string): Promise<ApiResponse<QuestionStatsDTO>> {
    return apiClient.get<QuestionStatsDTO>(`/surveys/${surveyId}/questions/stats`);
  },

  // Import questions
  async importQuestions(
    surveyId: string,
    data: ImportQuestionsDTO
  ): Promise<ApiResponse<ImportQuestionsResponseDTO>> {
    return apiClient.post<ImportQuestionsResponseDTO>(
      `/surveys/${surveyId}/questions/import`,
      data
    );
  },

  // Duplicate questions between surveys
  async duplicateQuestions(
    sourceSurveyId: string,
    targetSurveyId: string
  ): Promise<ApiResponse<QuestionListDTO>> {
    return apiClient.post<QuestionListDTO>(
      `/surveys/${sourceSurveyId}/questions/duplicate/${targetSurveyId}`
    );
  },

  // Template Question Management API

  // Get questions by template
  async getQuestionsByTemplate(
    tenantId: string,
    templateId: string
  ): Promise<ApiResponse<QuestionListDTO>> {
    return apiClient.get<QuestionListDTO>(
      `/tenants/${tenantId}/survey-templates/${templateId}/questions`
    );
  },

  // Get template question by ID
  async getTemplateQuestion(
    tenantId: string,
    templateId: string,
    questionId: string
  ): Promise<ApiResponse<QuestionDTO>> {
    return apiClient.get<QuestionDTO>(
      `/tenants/${tenantId}/survey-templates/${templateId}/questions/${questionId}`
    );
  },

  // Create template question
  async createTemplateQuestion(
    tenantId: string,
    templateId: string,
    data: CreateQuestionDTO
  ): Promise<ApiResponse<QuestionDTO>> {
    return apiClient.post<QuestionDTO>(
      `/tenants/${tenantId}/survey-templates/${templateId}/questions`,
      data
    );
  },

  // Update template question
  async updateTemplateQuestion(
    tenantId: string,
    templateId: string,
    questionId: string,
    data: UpdateQuestionDTO
  ): Promise<ApiResponse<QuestionDTO>> {
    return apiClient.put<QuestionDTO>(
      `/tenants/${tenantId}/survey-templates/${templateId}/questions/${questionId}`,
      data
    );
  },

  // Delete template question
  async deleteTemplateQuestion(
    tenantId: string,
    templateId: string,
    questionId: string
  ): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(
      `/tenants/${tenantId}/survey-templates/${templateId}/questions/${questionId}`
    );
  },

  // Reorder template questions
  async reorderTemplateQuestions(
    tenantId: string,
    templateId: string,
    data: ReorderQuestionsDTO
  ): Promise<ApiResponse<ReorderQuestionsResponseDTO>> {
    return apiClient.put<ReorderQuestionsResponseDTO>(
      `/tenants/${tenantId}/survey-templates/${templateId}/questions/reorder`,
      data
    );
  },
};
